/*
  ml-join — the join flow (sections/ml-join-flow.liquid), the J1 returning-guest modal
  (sections/ml-join-modal.liquid) and the join card (snippets/ml-join-card-cta.liquid).
  design_handoff_join_flow §6, §7, §9, §11.

  FLOW. The section prints every step; this picks one (§7 "Routing on load"):
    1. a member (or a customer whose $0 free-week order is under 8 days old) → "welcome" once
       if they came from this flow's checkout (ml_join.step was confirm / checkout started /
       ?step=welcome), otherwise "already";
    2. signed in, not a member: back from checkout without an order (?reason=checkout, or a
       checkout started from here) → confirm, fail mode (J3·2); a known plan → confirm, resume
       mode (J3·1) when they came from an email or an earlier session; no plan → plan;
    3. a guest: ?step, or the page default. Guests can't reach confirm unless they took the
       "skip" link (J-R3) or came back from checkout.
  State: sessionStorage ml_join {plan, step, src, started_at, checkout_at, guest} mirrored to
  localStorage ml_join_saved for 7 days (the "saved on this device" copy). The URL is kept in
  step: ?step=…&plan=… (pushState), so Back/Forward and email deep links work.

  CONFIRM STEP (setting, default OFF, 28 Sep): the flow is plan → account → checkout. Finishing
  the account step (email code, Shop, or "skip") runs the add path below first, then goes to
  Shopify's sign-in with return_to=/checkout (or straight to /checkout for "skip"); a signed-in
  non-member's plan Continue goes straight to checkout. ?step=confirm falls back to the account step.

  CHECKOUT (§9): /cart/add.js with the plan's trial variant + selling plan, quantity 1 →
  MlMembershipQty.normalize() (one membership line, the newest wins, product lines kept) →
  MlCheckoutGuard.check('join') (false → a role="alert" note, no redirect) → /cart/update.js
  attributes ml_join_src / ml_join_plan / ml_join_started (+ the UTM sync the attribution
  script already does) → /checkout. Nothing is ever added without that click.

  EMAIL (J2·2): validated, then posted once to /apps/precision/lead as
  {email, source: "join_plan_saved", plan, product_handle?} (the ESP3 hand-off for E1; the backend
  must map that source to a list before E1 can send), and ml_join_email_captured goes to
  analytics without the email. Then Shopify's sign-in: /customer_authentication/login with
  return_to=/pages/join?step=confirm&plan=… and login_hint=<email> (Shopify's OAuth accepts it).

  MODAL (§6): signed-out only (Liquid), 2nd+ session (localStorage ml_visits, counted once per
  sessionStorage session) or ?ml_join=1; never on cart/checkout/account/policies/join pages,
  never in the first N seconds, never when another modal ran this session (the campaign modal's
  sessionStorage ml_cm_shown, or any open dialog/overlay), never within the snooze window
  (localStorage ml_join_snooze_until), never once an account exists (ml_join_account). Opens
  after the delay AND the scroll depth, on home, product and collection pages, once per session.
*/
(function () {
  'use strict';
  if (window.MlJoin) return;

  var DAY = 86400000;
  var SAVE_DAYS = 7;
  var PLANS = ['monthly_trial', 'annual_trial'];
  var STEPS = ['signin', 'plan', 'account', 'confirm', 'welcome', 'already'];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- storage + analytics -------------------------------------------------------------- */
  function ls(k, v) {
    try {
      if (v === undefined) return window.localStorage.getItem(k);
      if (v === null) window.localStorage.removeItem(k);
      else window.localStorage.setItem(k, v);
    } catch (e) { return null; }
  }
  function ss(k, v) {
    try {
      if (v === undefined) return window.sessionStorage.getItem(k);
      if (v === null) window.sessionStorage.removeItem(k);
      else window.sessionStorage.setItem(k, v);
    } catch (e) { return null; }
  }
  function readJson(raw) { try { return JSON.parse(raw || 'null'); } catch (e) { return null; } }

  function utm() {
    var rec = readJson(ls('monthlees_attr_v1')) || {};
    var out = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
      var v = rec[k] || rec['last_' + k] || rec['first_' + k];
      if (v) out[k] = String(v).slice(0, 120);
    });
    return out;
  }
  function track(name, props) {
    var p = Object.assign({}, utm(), props || {});
    try {
      if (window.MonthleesAnalytics && typeof window.MonthleesAnalytics.track === 'function') window.MonthleesAnalytics.track(name, p);
      else if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, p);
    } catch (e) { /* never break the page */ }
  }

  /* ---- bookkeeping on every page (modal eligibility + last viewed) ----------------------- */
  var customer = window.MonthleesCustomer || {};
  if (customer.loggedIn) ls('ml_join_account', '1');
  if (!ss('ml_visit_counted')) {
    ss('ml_visit_counted', '1');
    ls('ml_visits', String((parseInt(ls('ml_visits'), 10) || 0) + 1));
  }
  var prevLastViewed = ls('ml_last_viewed');
  (function () {
    var meta = window.MonthleesPageMeta || {};
    var h = meta.product && meta.product.handle;
    if (!h) {
      var dlg = document.querySelector('[data-ml-join-modal]');
      h = dlg && dlg.getAttribute('data-product-handle');
    }
    if (h && !/-member$|membership|trial/.test(h)) ls('ml_last_viewed', h);
  })();

  document.addEventListener('click', function (e) {
    var card = e.target && e.target.closest && e.target.closest('[data-ml-join-card]');
    if (card) track('ml_join_card_click', { surface: card.getAttribute('data-ml-join-card') || '' });
  });

  /* ======================================================================================= */
  /* FLOW                                                                                    */
  /* ======================================================================================= */
  function initFlow(root) {
    var cfgNode = root.querySelector('[data-jf-config]');
    var cfg = readJson(cfgNode && cfgNode.textContent) || {};
    if (window.Shopify && window.Shopify.designMode) return; // the editor shows the server's default step
    root.classList.add('is-js');

    var state = loadState();
    var current = { step: null, mode: 'confirm', plan: null };
    var signinReturn = '';

    function loadState() {
      var s = readJson(ss('ml_join'));
      var fromSession = !!s;
      if (!s) {
        var saved = readJson(ls('ml_join_saved'));
        if (saved && saved.saved_at && Date.now() - saved.saved_at < SAVE_DAYS * DAY) s = saved;
        else if (saved) ls('ml_join_saved', null);
      }
      s = s || {};
      s._fromSession = fromSession;
      return s;
    }
    function saveState() {
      var out = { plan: state.plan, step: state.step, src: state.src || '', started_at: state.started_at || Date.now(), checkout_at: state.checkout_at || 0, guest: !!state.guest };
      state.started_at = out.started_at;
      ss('ml_join', JSON.stringify(out));
      out.saved_at = Date.now();
      ls('ml_join_saved', JSON.stringify(out));
    }
    function clearState() {
      state = { _fromSession: true };
      ss('ml_join', null);
      ls('ml_join_saved', null);
    }

    function planOk(p) { return p && cfg.plans && cfg.plans[p] && cfg.plans[p].ok; }
    function pickPlan(p) {
      if (planOk(p)) return p;
      if (planOk(cfg.defaultPlan)) return cfg.defaultPlan;
      for (var i = 0; i < PLANS.length; i++) if (planOk(PLANS[i])) return PLANS[i];
      return cfg.defaultPlan || 'monthly_trial';
    }

    function params() { return new URLSearchParams(window.location.search); }

    /* ---- routing ---- */
    function route() {
      var q = params();
      var urlStep = q.get('step');
      if (STEPS.indexOf(urlStep) === -1) urlStep = '';
      if (!urlStep && window.location.hash) {
        var h = window.location.hash.slice(1);
        if (STEPS.indexOf(h) !== -1) urlStep = h;
      }
      var urlPlan = PLANS.indexOf(q.get('plan')) !== -1 ? q.get('plan') : '';
      var reason = q.get('reason') || '';
      var src = q.get('src') || state.src || '';
      if (q.get('src')) state.src = q.get('src');
      var fromEmail = q.get('utm_source') === 'esp3' || /^(e\d|email)/.test(q.get('src') || '');
      var plan = pickPlan(urlPlan || state.plan);
      var step, mode = 'confirm';
      var checkoutRecent = state.checkout_at && Date.now() - state.checkout_at < 2 * DAY;

      if (cfg.member) {
        var cameFromCheckout = state.step === 'confirm' || checkoutRecent || urlStep === 'welcome';
        if (cameFromCheckout && ls('ml_join_welcomed') !== '1' && (cfg.trialLive || checkoutRecent)) {
          step = 'welcome';
        } else {
          step = 'already';
        }
      } else if (cfg.loggedIn && !cfg.confirmStep) {
        // No confirm step: the account is done, so the plan step's Continue goes straight to checkout.
        step = 'plan';
        if (reason === 'checkout' || (checkoutRecent && urlStep !== 'plan')) mode = 'fail';
      } else if (!cfg.loggedIn && !cfg.confirmStep) {
        if (reason === 'checkout' || (checkoutRecent && (urlStep === 'confirm' || urlStep === 'account' || !urlStep))) {
          step = 'account'; mode = 'fail';
        } else if (urlStep === 'confirm' || urlStep === 'account') {
          step = 'account'; // ?step=confirm falls back to the account step
        } else if (urlStep === 'welcome') {
          step = 'signin';
          signinReturn = cfg.join + '?step=welcome';
        } else if (urlStep === 'signin' || urlStep === 'plan') {
          step = urlStep;
        } else {
          step = cfg.defaultStep === 'signin' ? 'signin' : 'plan';
        }
      } else if (cfg.loggedIn) {
        if (urlStep === 'plan') {
          step = 'plan';
        } else if (reason === 'checkout' || (checkoutRecent && urlStep !== 'plan')) {
          step = 'confirm'; mode = 'fail';
        } else if (urlPlan || state.plan || urlStep === 'confirm') {
          step = 'confirm';
          if (fromEmail || (!state._fromSession && state.plan)) mode = 'resume';
        } else {
          step = 'plan';
        }
      } else {
        var guestOk = cfg.allowGuest && (q.get('guest') === '1' || state.guest);
        if (reason === 'checkout' || (checkoutRecent && (urlStep === 'confirm' || !urlStep))) {
          step = 'confirm'; mode = 'fail';
          state.guest = true;
        } else if (urlStep === 'confirm') {
          step = guestOk ? 'confirm' : 'account';
          if (guestOk) state.guest = true;
        } else if (urlStep === 'welcome') {
          // Back from checkout as a guest: an order can't be seen until they sign in.
          step = 'signin';
          signinReturn = cfg.join + '?step=welcome';
        } else if (urlStep === 'signin' || urlStep === 'plan' || urlStep === 'account') {
          step = urlStep;
        } else {
          step = cfg.defaultStep === 'signin' ? 'signin' : 'plan';
        }
      }
      if (step === 'signin' && cfg.loggedIn) {
        var rt = safeReturn(q.get('return_to')) || '/account';
        window.location.replace(rt);
        return;
      }
      show(step, mode, plan, { initial: true, src: src, reason: reason });
    }

    function safeReturn(v) {
      if (!v || typeof v !== 'string') return '';
      if (v.charAt(0) !== '/' || v.charAt(1) === '/' || v.charAt(1) === '\\') return '';
      return v;
    }

    /* ---- rendering ---- */
    function tokens(plan) {
      var p = cfg.plans[plan] || {};
      var annual = plan === 'annual_trial';
      var per = annual ? cfg.yr : cfg.mo;
      return Object.assign({}, cfg.tokens || {}, { plan_price: (p.price || '') + per, plan_label: p.label || '', plan_per: per, trial_end: trialEnd() });
    }
    function trialEnd() {
      var d = new Date(Date.now() + (cfg.trialDays || 7) * DAY);
      try { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch (e) { return d.toDateString(); }
    }
    function fill(plan) {
      var t = tokens(plan);
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-tpl]'), function (el) {
        var tpl = el.getAttribute('data-jf-tpl');
        el.textContent = tpl.replace(/\{([a-z_]+)\}/g, function (m, k) { return t[k] != null ? t[k] : m; });
      });
      var p = cfg.plans[plan] || {};
      var v = root.querySelector('[data-jf-variant]');
      var sp = root.querySelector('[data-jf-selling-plan]');
      if (v && p.variant) v.value = p.variant;
      if (sp && p.sellingPlan) sp.value = p.sellingPlan;
      var rc = cfg.confirmStep ? cfg.join + '?step=confirm&plan=' + plan + (state.src ? '&src=' + encodeURIComponent(state.src) : '') : '/checkout';
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-email-form="account"] [data-jf-return]'), function (i) { i.value = rc; });
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-shop="account"]'), function (a) { a.href = loginUrl(rc); });
      var sr = signinReturn || safeReturn(params().get('return_to')) || '/account';
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-email-form="signin"] [data-jf-return]'), function (i) { i.value = sr; });
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-shop="signin"]'), function (a) { a.href = loginUrl(sr); });
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-signin-link]'), function (a) { a.href = loginUrl(cfg.join); });
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-plan]'), function (r) { r.checked = planOf(r) === plan; });
    }
    function planOf(r) { return r.getAttribute('data-plan') || r.value; }
    function loginUrl(ret, email) {
      var u = cfg.login + '?return_to=' + encodeURIComponent(ret);
      if (email && cfg.loginHint) u += '&login_hint=' + encodeURIComponent(email);
      return u;
    }

    function show(step, mode, plan, opts) {
      opts = opts || {};
      var prev = current.step;
      current = { step: step, mode: mode || 'confirm', plan: plan };
      state.plan = plan;
      if (step !== 'welcome' && step !== 'already') state.step = step;
      if (!state.started_at && (step === 'plan' || step === 'account' || step === 'confirm')) state.started_at = Date.now();

      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-step]'), function (el) {
        var on = el.getAttribute('data-jf-step') === step;
        el.classList.toggle('is-active', on);
        if (el.getAttribute('data-jf-step') === 'confirm') el.setAttribute('data-mode', current.mode);
      });
      var cap = step === 'confirm' && current.mode !== 'confirm' ? current.mode : step;
      Array.prototype.forEach.call(root.querySelectorAll('.jf-side__cap'), function (el) {
        el.classList.toggle('is-active', el.getAttribute('data-for') === cap);
      });
      root.setAttribute('data-step', step);
      root.classList.toggle('is-fail', current.mode === 'fail' && step !== 'confirm');
      fill(plan);
      Array.prototype.forEach.call(root.querySelectorAll('[data-jf-checkout-msg]'), function (msg) { msg.hidden = true; msg.textContent = ''; });

      // URL: keep every other param (utm_*, src…), set step + plan, drop one-shot flags.
      var q = params();
      q.set('step', step);
      if (step === 'plan' || step === 'account' || step === 'confirm') q.set('plan', plan); else q.delete('plan');
      if (current.mode !== 'fail') q.delete('reason');
      q.delete('guest');
      var url = window.location.pathname + '?' + q.toString();
      try {
        if (opts.initial || opts.pop) window.history.replaceState({ jf: step }, '', url);
        else window.history.pushState({ jf: step }, '', url);
      } catch (e) {}

      if (step === 'welcome') {
        ls('ml_join_welcomed', '1');
        track('ml_join_welcome', { plan: plan, latency_from_start_s: state.started_at ? Math.round((Date.now() - state.started_at) / 1000) : null, src: state.src || '' });
        clearState();
      } else if (step !== 'already') {
        saveState();
      }
      if (current.mode === 'fail') track('ml_join_fail_view', { reason: opts.reason || 'checkout', plan: plan, src: state.src || '' });
      track('ml_join_step_view', { step: step === 'confirm' && current.mode !== 'confirm' ? current.mode : step, plan: plan, src: state.src || '' });

      if (!opts.initial && prev !== step) {
        var h = root.querySelector('[data-jf-step="' + step + '"] .jf-h');
        if (h) { try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    /* ---- events ---- */
    root.addEventListener('change', function (e) {
      var r = e.target;
      if (r && r.matches && r.matches('[data-jf-plan]') && r.checked) {
        var rp = planOf(r);
        current.plan = rp;
        state.plan = rp;
        fill(rp);
        saveState();
        try {
          var q = params(); q.set('plan', rp);
          window.history.replaceState(window.history.state, '', window.location.pathname + '?' + q.toString());
        } catch (x) {}
        track('ml_join_plan_select', { plan: rp, src: state.src || '' });
      }
    });

    root.addEventListener('submit', function (e) {
      var f = e.target;
      if (f.matches('[data-jf-plan-form]')) {
        e.preventDefault();
        var checked = f.querySelector('[data-jf-plan]:checked');
        var plan = pickPlan(checked ? planOf(checked) : current.plan);
        if (!cfg.confirmStep && cfg.loggedIn) {
          current.plan = plan;
          var sb = e.submitter || f.querySelector('.jf-go--' + plan) || f.querySelector('[data-jf-plan-continue]');
          addAndGo(plan, sb, '/checkout');
          return;
        }
        show(cfg.loggedIn ? 'confirm' : 'account', 'confirm', plan);
        return;
      }
      if (f.matches('[data-jf-email-form]')) {
        e.preventDefault();
        onEmail(f, e.submitter);
        return;
      }
      if (f.matches('[data-jf-checkout]')) {
        e.preventDefault();
        onCheckout(f);
      }
    });

    root.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('a');
      if (!a || !root.contains(a) || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      var go = a.getAttribute('data-jf-go');
      if (go) {
        e.preventDefault();
        if (a.hasAttribute('data-jf-reset')) { var keep = current.plan; clearState(); state.plan = keep; }
        if (a.hasAttribute('data-jf-guest')) state.guest = true;
        show(go, 'confirm', current.plan);
        return;
      }
      if (a.hasAttribute('data-jf-shop')) {
        track('ml_join_shop_click', { step: current.step, plan: current.plan, src: state.src || '' });
        if (!cfg.confirmStep && a.getAttribute('data-jf-shop') === 'account') {
          e.preventDefault();
          addAndGo(pickPlan(current.plan), a, loginUrl('/checkout'), cfg.msg.busySignin);
        }
      }
    });

    // "Skip: create it at checkout" is a plain /checkout link for no-JS visitors (their plan form
    // already added the trial). With JS it runs the add path first. Window capture runs before the
    // checkout guard's document-capture link handler, which would otherwise go to /checkout
    // without the trial.
    window.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('[data-jf-skip-checkout]');
      if (!a || !root.contains(a) || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      state.guest = true;
      track('ml_join_skip_account', { plan: current.plan, src: state.src || '' });
      addAndGo(pickPlan(current.plan), a, '/checkout');
    }, true);

    window.addEventListener('popstate', function () { state = loadState(); state._fromSession = true; route(); });
    // Back from Shopify checkout restores the page from the bfcache: re-route (fail, if no order).
    window.addEventListener('pageshow', function (e) { if (e.persisted) { clearBusy(); state = loadState(); state._fromSession = true; route(); } });

    function onEmail(form, submitter) {
      var input = form.querySelector('input[type="email"]');
      var err = form.querySelector('[data-jf-err]');
      var email = String((input && input.value) || '').trim();
      if (!EMAIL_RE.test(email)) {
        if (input) { input.setAttribute('aria-invalid', 'true'); input.focus(); }
        if (err) err.textContent = (cfg.msg && cfg.msg.emailError) || 'Enter a full email address.';
        return;
      }
      if (input) input.removeAttribute('aria-invalid');
      if (err) err.textContent = '';
      var kind = form.getAttribute('data-jf-email-form');
      var ret = form.querySelector('[data-jf-return]');
      var dest = loginUrl(ret ? ret.value : '/account', email);
      if (kind === 'account') {
        var body = { email: email, source: 'join_plan_saved', plan: current.plan };
        var last = ls('ml_last_viewed');
        if (last) body.product_handle = last;
        try {
          window.fetch('/apps/precision/lead', {
            method: 'POST', credentials: 'same-origin', keepalive: true,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          }).catch(function () {});
        } catch (x) {}
        track('ml_join_email_captured', { src: state.src || '', plan: current.plan });
        saveState();
        if (!cfg.confirmStep) {
          addAndGo(pickPlan(current.plan), submitter || form.querySelector('[data-jf-account-continue]'), loginUrl('/checkout', email), cfg.msg.busySignin);
          return;
        }
      } else {
        track('ml_join_signin_submit', { surface: 'signin' });
      }
      window.location.assign(dest);
    }

    /* ---- checkout (§9) ---- */
    var busy = null;
    function setBusy(btn, label) {
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('data-label', btn.innerHTML);
      btn.textContent = label || (cfg.msg && cfg.msg.busy) || 'Taking you to checkout…';
      // Like Express: 10 s and it resets, with the error so they can try again.
      busy = { btn: btn, timer: setTimeout(function () { clearBusy(cfg.msg && cfg.msg.error); }, 10000) };
    }
    function clearBusy(message) {
      if (!busy) return;
      var b = busy; busy = null;
      clearTimeout(b.timer);
      b.btn.removeAttribute('aria-disabled');
      b.btn.removeAttribute('aria-busy');
      if (b.btn.hasAttribute('data-label')) { b.btn.innerHTML = b.btn.getAttribute('data-label'); b.btn.removeAttribute('data-label'); }
      if (message) {
        var scope = (b.btn.closest && b.btn.closest('[data-jf-step]')) || root;
        var m = scope.querySelector('[data-jf-checkout-msg]') || root.querySelector('.jf-step.is-active [data-jf-checkout-msg]');
        if (m) { m.textContent = message; m.hidden = false; }
      }
    }
    function postJson(url, body) {
      return window.fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (d) {
          if (!r.ok) throw new Error((d && (d.description || d.message)) || ('HTTP ' + r.status));
          return d;
        });
      });
    }
    function onCheckout(form) {
      if (busy) return;
      if (typeof window.fetch !== 'function') { form.submit(); return; }
      addAndGo(pickPlan(current.plan), form.querySelector('[data-jf-checkout-btn]'), '/checkout');
    }

    /* The one add path (§9): the trial line (replacing any membership line, products kept), the qty
       guard, the checkout guard, the ml_join_* attributes + UTM sync, then `dest` (/checkout, or
       Shopify's sign-in returning to /checkout when there's no confirm step). */
    function addAndGo(plan, btn, dest, busyLabel) {
      if (busy || !btn) return;
      if (typeof window.fetch !== 'function') { window.location.assign(dest); return; }
      var p = cfg.plans[plan] || {};
      var scope = (btn.closest && btn.closest('[data-jf-step]')) || root;
      var m0 = scope.querySelector('[data-jf-checkout-msg]');
      if (m0) { m0.hidden = true; m0.textContent = ''; }
      setBusy(btn, busyLabel);
      var mine = busy;
      if (!p.variant || !p.sellingPlan) { clearBusy(cfg.msg.error); return; }
      var started = state.started_at || Date.now();
      track('ml_join_checkout_click', { plan: plan, src: state.src || '', mode: current.mode, step: current.step });
      postJson('/cart/add.js', { items: [{ id: Number(p.variant), quantity: 1, selling_plan: Number(p.sellingPlan) }] })
        .then(function () {
          document.dispatchEvent(new CustomEvent('monthlees:cart-updated', { detail: { source: 'ml-join' } }));
          return window.MlMembershipQty && typeof window.MlMembershipQty.normalize === 'function' ? window.MlMembershipQty.normalize() : null;
        })
        .then(function () {
          return window.MlCheckoutGuard && typeof window.MlCheckoutGuard.check === 'function' ? window.MlCheckoutGuard.check('join') : true;
        })
        .then(function (ok) {
          if (busy !== mine) return null;
          if (ok === false) {
            track('ml_join_checkout_blocked', { plan: plan, reason: 'member_price_guard' });
            clearBusy(cfg.msg.blocked || cfg.msg.error);
            return null;
          }
          return postJson('/cart/update.js', { attributes: { ml_join_src: state.src || 'direct', ml_join_plan: plan, ml_join_started: new Date(started).toISOString() } })
            .then(function () {
              var attr = window.MonthleesAttribution;
              return attr && typeof attr.syncToCart === 'function' ? attr.syncToCart(true).catch(function () {}) : null;
            })
            .then(function () {
              if (busy !== mine) return;
              state.checkout_at = Date.now();
              state.step = 'confirm';
              saveState();
              window.location.assign(dest);
            });
        })
        .catch(function (err) {
          track('ml_join_checkout_blocked', { plan: plan, reason: String((err && err.message) || err).slice(0, 160) });
          if (busy === mine) clearBusy(cfg.msg.error);
        });
    }

    route();
  }

  /* ======================================================================================= */
  /* J1 MODAL                                                                                */
  /* ======================================================================================= */
  function initModal(dlg) {
    if (window.Shopify && window.Shopify.designMode) return;
    if (typeof dlg.showModal !== 'function') return;
    var q = new URLSearchParams(window.location.search);
    var force = q.get('ml_join') === '1';
    var path = window.location.pathname;
    var pageType = dlg.getAttribute('data-page-type') || '';
    var minSessions = parseInt(dlg.getAttribute('data-min-sessions'), 10) || 2;
    var delay = (parseFloat(dlg.getAttribute('data-delay')) || 5) * 1000;
    var scrollPct = parseFloat(dlg.getAttribute('data-scroll'));
    if (isNaN(scrollPct)) scrollPct = 40;
    var snoozeDays = parseFloat(dlg.getAttribute('data-snooze')) || 7;
    var variant = 'b';
    var product = '';

    function excludedPath() {
      return /^\/(cart|checkout|checkouts|account|policies)(\/|$)/.test(path) || /^\/pages\/(join|sign-in)/.test(path) || /\/(cart|checkouts?)\b/.test(path);
    }
    function otherModalRan() {
      if (ss('ml_cm_shown') === '1') return true;
      if (document.querySelector('dialog[open]:not([data-ml-join-modal])')) return true;
      var overlays = document.querySelectorAll('.monthlees-modal-overlay, [data-monthlees-modal-overlay], .ml-edg, [data-ml-email-drawer]');
      for (var i = 0; i < overlays.length; i++) {
        var o = overlays[i];
        if (!o.hidden && (o.classList.contains('is-open') || o.getAttribute('aria-hidden') === 'false' || o.hasAttribute('open'))) return true;
      }
      return false;
    }
    function eligible(atTrigger) {
      if (excludedPath()) return false;
      if (['index', 'product', 'collection'].indexOf(pageType) === -1) return false;
      if (ls('ml_join_account') === '1') return false;
      if (document.body && document.body.getAttribute('data-monthlees-membership-in-cart') === 'true') return false;
      if (ss('ml_join_shown') === '1' && !force) return false;
      if (!force) {
        if ((parseInt(ls('ml_visits'), 10) || 0) < minSessions) return false;
        var until = parseInt(ls('ml_join_snooze_until'), 10);
        if (until && Date.now() < until) return false;
      }
      if (atTrigger && otherModalRan()) return false;
      return true;
    }
    if (!eligible(false)) return;

    var timeOk = false, scrollOk = scrollPct <= 0, fired = false;
    function depth() {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h <= 0) return 100;
      return (window.scrollY / h) * 100;
    }
    function maybe() {
      if (fired || !timeOk || !scrollOk) return;
      fired = true;
      window.removeEventListener('scroll', onScroll);
      if (!eligible(true)) return;
      open();
    }
    function onScroll() { if (!scrollOk && depth() >= scrollPct) { scrollOk = true; maybe(); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(function () { timeOk = true; onScroll(); maybe(); }, delay);

    function loadCss() {
      return new Promise(function (resolve) {
        var href = dlg.getAttribute('data-css');
        if (!href || document.querySelector('link[data-ml-join-css]')) return resolve();
        var l = document.createElement('link');
        l.rel = 'stylesheet'; l.href = href; l.setAttribute('data-ml-join-css', '');
        l.onload = l.onerror = function () { resolve(); };
        document.head.appendChild(l);
        setTimeout(resolve, 3000);
      });
    }
    function loadProduct() {
      var h = prevLastViewed;
      if (dlg.getAttribute('data-last-viewed') === 'false' || !h) return Promise.resolve(null);
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return window.fetch(root + 'products/' + encodeURIComponent(h) + '?section_id=ml-join-modal', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('http'); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var t = doc.querySelector('template[data-jm-product]');
          if (!t || t.getAttribute('data-ok') !== 'true') return null;
          return { handle: h, node: t.content };
        })
        .catch(function () { return null; });
    }
    function open() {
      Promise.all([loadCss(), loadProduct()]).then(function (res) {
        var p = res[1];
        var card = dlg.querySelector('[data-jm-card]');
        var slot = dlg.querySelector('[data-jm-prod]');
        if (p && slot) {
          slot.innerHTML = '';
          slot.appendChild(document.importNode(p.node, true));
          slot.hidden = false;
          variant = 'a'; product = p.handle;
        } else if (slot) {
          slot.hidden = true;
        }
        card.setAttribute('data-variant', variant);
        var cta = dlg.querySelector('[data-jm-cta]');
        if (cta) cta.href = (cta.getAttribute('data-join') || '/pages/join') + '?step=plan&src=j1' + variant + '#plan';
        var si = dlg.querySelector('[data-jm-signin]');
        if (si) si.href = (si.getAttribute('data-login') || '/customer_authentication/login') + '?return_to=' + encodeURIComponent(window.location.pathname + window.location.search.replace(/([?&])ml_join=1&?/, '$1').replace(/[?&]$/, ''));
        if (otherModalRan()) return;
        ss('ml_join_shown', '1');
        ss('ml_cm_shown', '1'); // one modal per session: the campaign modal reads this flag
        document.documentElement.classList.add('ml-jm-lock');
        dlg.showModal();
        var h = dlg.querySelector('#jm-h');
        if (h) { try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
        var props = { variant: variant };
        if (product) props.product_handle = product;
        track('ml_join_modal_view', props);
      });
    }
    function close(method) {
      if (!dlg.open) return;
      track('ml_join_modal_dismiss', { variant: variant, method: method });
      dlg.close();
    }
    dlg.addEventListener('close', function () { document.documentElement.classList.remove('ml-jm-lock'); });
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close('esc'); });
    dlg.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-jm-close]')) { close('x'); return; }
      if (t.closest('[data-jm-snooze]')) {
        e.preventDefault();
        ls('ml_join_snooze_until', String(Date.now() + snoozeDays * DAY));
        close('not_now');
        return;
      }
      if (t.closest('[data-jm-cta]')) { track('ml_join_modal_cta', { variant: variant }); return; }
      if (t.closest('[data-jm-signin]')) { track('ml_join_modal_signin', { variant: variant }); return; }
      if (t === dlg || t.classList.contains('jm__wrap')) close('backdrop');
    });

    window.MlJoin.openModal = function () { force = true; open(); };
  }

  window.MlJoin = { track: track };
  function init() {
    var root = document.querySelector('[data-ml-join-flow]');
    if (root) initFlow(root);
    var dlg = document.querySelector('[data-ml-join-modal]');
    if (dlg) initModal(dlg);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
